import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  FileText, LogOut, Home, Settings as SettingsIcon,
  Calendar, ListChecks, AlertTriangle, FolderKanban, LayoutGrid, Menu, X,
  Search, Grid3X3, Trash2, ChevronDown, ChevronRight, Target, Zap, ExternalLink, Wallet,
  Sparkles, Rocket, Stethoscope,
} from "lucide-react";
import { useState } from "react";
import { useApp } from "../lib/AppContext";
import { api } from "../lib/api";
import Tip from "./Tip";
import ChatbotWidget from "./ChatbotWidget";
import MobileTabBar from "./MobileTabBar";
import NotificationBell from "./NotificationBell";
import GdprNoticeModal from "./GdprNoticeModal";
import BackButton from "./BackButton";

const PRIMARY_LINKS = [
  { to: "/", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/agenda", label: "Agenda", icon: Calendar, testid: "nav-agenda" },
  { to: "/cicli", label: "Cicli", icon: ListChecks, testid: "nav-cicli" },
  { to: "/deadlines", label: "Le mie scadenze", icon: AlertTriangle, testid: "nav-deadlines" },
  { to: "/segretaria", label: "Segretaria IA", icon: Sparkles, testid: "nav-segretaria" },
];

// Task Cicli · Fase 1 — sub-menu removed in favour of /cicli landing.
// Kept as a "utility" list still linkable from the Cicli page footer.
const TASKS_UTILITY_LINKS = [
  { to: "/tasks/matrix", label: "Matrice U/I", icon: Grid3X3, testid: "nav-cicli-matrix" },
  { to: "/tasks/search", label: "Ricerca", icon: Search, testid: "nav-cicli-search" },
  { to: "/tasks/trash", label: "Cestino", icon: Trash2, testid: "nav-cicli-trash" },
];

const TAIL_LINKS = [
  { to: "/projects", label: "Progetti", icon: FolderKanban, testid: "nav-projects" },
  { to: "/life-areas", label: "Aree", icon: LayoutGrid, testid: "nav-areas" },
  { to: "/mete", label: "Mete", icon: Target, testid: "nav-mete" },
  { to: "/azioni", label: "Azioni", icon: Zap, testid: "nav-azioni" },
];

const DOCUMENTS_SUBLINKS = [
  { to: "/documenti", label: "Tutti i documenti", testid: "nav-documents-all", end: true },
  { to: "/documenti?category=fattura", label: "Fatture", testid: "nav-documents-fatture" },
  { to: "/documenti?category=ricevuta", label: "Ricevute", testid: "nav-documents-ricevute" },
  { to: "/documenti?category=contratto", label: "Contratti", testid: "nav-documents-contratti" },
  { to: "/documenti?category=estratto_conto", label: "Estratti conto", testid: "nav-documents-estratti" },
  { to: "/documenti?category=referto_amministrativo", label: "Referti amministrativi", testid: "nav-documents-referti" },
  { to: "/documents/accountant", label: "Fatture commercialista", testid: "nav-documents-accountant", financeOnly: true },
  { to: "/documenti/cestino", label: "Cestino", testid: "nav-documents-trash" },
];

const FINANCES_SUBLINKS = [
  { to: "/finances", label: "Dashboard", testid: "nav-finances-dashboard", end: true },
  { to: "/finances/items", label: "Tutte le voci", testid: "nav-finances-items" },
  { to: "/finances/items?type=income_expected,income_received", label: "Entrate", testid: "nav-finances-income" },
  { to: "/finances/items?type=fixed_monthly,fixed_annual", label: "Uscite fisse", testid: "nav-finances-fixed" },
  { to: "/finances/items?type=tax,contribution", label: "Tasse e contributi", testid: "nav-finances-tax" },
  { to: "/finances/items?type=credit_card_payment", label: "Carte di credito", testid: "nav-finances-cards" },
  { to: "/finances/items?type=subscription", label: "Abbonamenti", testid: "nav-finances-subs" },
  { to: "/finances/items?type=pac", label: "PAC", testid: "nav-finances-pac" },
  { to: "/finances/accounts", label: "Conti bancari", testid: "nav-finances-accounts" },
  { to: "/finances/reports", label: "Report PDF", testid: "nav-finances-reports" },
];

const PRIMEVISION_SUBLINKS = [
  { to: "/prime-vision/crm", label: "CRM / Lead", testid: "nav-pv-crm", end: true },
  { to: "/prime-vision/crm/activities", label: "Attività CRM", testid: "nav-pv-activities" },
];

const MEDICO_SUBLINKS = [
  { to: "/medico/formazione", label: "Formazione ECM", testid: "nav-medico-formazione", end: true },
  { to: "/medico/formazione?category=congresso", label: "Congressi", testid: "nav-medico-congressi" },
  { to: "/medico/assicurazioni", label: "Assicurazioni professionali", testid: "nav-medico-assicurazioni" },
  { to: "/finances/items?tag=enpam", label: "ENPAM", testid: "nav-medico-enpam" },
];

const EXTERNAL_LINKS = [
  {
    href: "https://kersystem.it",
    label: "KERSystem",
    icon: ExternalLink,
    testid: "sidebar-external-kersystem",
    tooltip: "Apri KERSystem.it in una nuova scheda",
  },
];

export default function Shell({ children }) {
  const { config, user, refresh } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    try { await api.logout(); } catch {}
    await refresh();
    nav("/login");
  };

  const short = config?.app_short_name || "Agenda Push";
  const name = config?.app_name || "Agenda Push";

  return (
    <div className="min-h-dvh bg-white flex" data-testid="app-shell">
      {/* Desktop sidebar — visibile ≥ md (768px) */}
      <aside
        className="hidden md:flex flex-col w-64 border-r sticky top-0 h-dvh"
        style={{ borderColor: "var(--pxos-border)", background: "linear-gradient(180deg, var(--pxos-primary-light) 0%, #FFFFFF 40%)" }}
        data-testid="sidebar-desktop"
      >
        <BrandBlock short={short} name={name} />
        <NavList onNavigate={() => {}} isOwner={user?.is_owner} roles={user?.roles || []} />
        <UserFooter user={user} onLogout={onLogout} />
      </aside>

      {/* Mobile top bar — solo < md; il menu hamburger legacy resta come fallback */}
      <div className="md:hidden fixed top-0 inset-x-0 z-30 border-b"
           style={{ borderColor: "var(--pxos-border)", background: "linear-gradient(90deg, var(--pxos-primary) 0%, var(--pxos-primary-light) 100%)" }}>
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="font-display text-base font-semibold" style={{ color: "var(--pxos-text-dark)" }}>{name}</div>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Tip label="Esci dall'account" side="bottom">
              <button onClick={onLogout} className="p-2 -mr-2" data-testid="mobile-logout" aria-label="Esci">
                <LogOut size={20} style={{ color: "var(--pxos-text-dark)" }} />
              </button>
            </Tip>
          </div>
        </div>
      </div>

      {/* Main area */}
      <main className="flex-1 min-w-0" data-testid="app-main">
        <div
          className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 py-6 sm:py-10 pt-16 md:pt-10"
          style={{ paddingBottom: "calc(64px + env(safe-area-inset-bottom) + 16px)" }}
        >
          {/* Blocco 3 · B · BackButton universale (auto-nascosto sulle root/pubbliche) */}
          <BackButton auto />
          {children}
        </div>
      </main>

      {/* Bottom Tab Bar mobile — solo < md */}
      <MobileTabBar onOpenChatbot={() => window.dispatchEvent(new CustomEvent("pxos:open-chatbot"))} />

      {/* Segretaria IA (FAB + drawer) — nascosto su /segretaria (già full-page) */}
      {!loc.pathname.startsWith("/segretaria") && <ChatbotWidget />}

      {/* GDPR one-time notice popup (Task C2) */}
      <GdprNoticeModal />
    </div>
  );
}

function BrandBlock({ short, name }) {
  const { config } = useApp();
  const iconUrl = config?.brand_icon_url || "/branding/icon-192.png";
  return (
    <div className="px-5 py-6 border-b flex items-center gap-3" style={{ borderColor: "var(--pxos-border)" }}>
      <img
        src={iconUrl}
        alt={name}
        width={40}
        height={40}
        className="h-10 w-10 rounded-full object-cover"
        style={{ background: "white", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
        data-testid="sidebar-brand-logo"
      />
      <div className="leading-tight min-w-0 flex-1">
        <div className="font-display text-base font-semibold truncate" style={{ color: "var(--pxos-text-dark)" }}>{name}</div>
        <div className="text-[10px] uppercase tracking-widest opacity-70" style={{ color: "var(--pxos-text-dark)" }}>
          Fase 2 · Executive OS
        </div>
      </div>
      <div className="hidden md:block ml-1">
        <NotificationBell />
      </div>
    </div>
  );
}

function NavList({ onNavigate, isOwner, roles = [] }) {
  const loc = useLocation();
  // Finanze section is visible only to the workspace owner and to users
  // with the admin_finance_hr role — mirrors the backend router guard.
  const canSeeFinances = Boolean(isOwner) || roles.includes("admin_finance_hr");
  const financesActive = loc.pathname === "/finances" || loc.pathname.startsWith("/finances/");
  const documentsActive = loc.pathname === "/documenti" || loc.pathname.startsWith("/documenti/");
  const [financesOpen, setFinancesOpen] = useState(financesActive);
  const [documentsOpen, setDocumentsOpen] = useState(documentsActive);
  return (
    <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" data-testid="nav-list">
      {PRIMARY_LINKS.map((l) => <SideNavItem key={l.to} {...l} onNavigate={onNavigate} />)}
      {TAIL_LINKS.map((l) => <SideNavItem key={l.to} {...l} onNavigate={onNavigate} />)}
      {/* Finanze expandable — hidden for roles without finance access. */}
      {canSeeFinances && (<>
      <button
        type="button"
        onClick={() => setFinancesOpen((v) => !v)}
        className={[
          "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition w-full",
          financesActive ? "bg-white shadow-sm" : "hover:bg-white/70",
        ].join(" ")}
        style={{ color: "var(--pxos-text-dark)" }}
        data-testid="nav-finances-group"
        title="Espandi/comprimi il menu Finanze"
        aria-expanded={financesOpen}
      >
        <Wallet size={17}/>
        <span className="truncate flex-1 text-left">Finanze</span>
        {financesOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
      </button>
      {financesOpen && (
        <div className="pl-4 border-l ms-3.5 space-y-0.5" style={{ borderColor: "var(--pxos-border)" }} data-testid="nav-finances-submenu">
          {FINANCES_SUBLINKS.map((l) => (
            <NavLink
              key={l.testid}
              to={l.to}
              end={l.end}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                ["flex items-center gap-2 rounded-xl px-3 py-1.5 text-[13px] transition",
                 isActive ? "bg-white shadow-sm font-semibold" : "hover:bg-white/70"].join(" ")
              }
              style={{ color: "var(--pxos-text-dark)" }}
            >
              <span className="truncate">{l.label}</span>
            </NavLink>
          ))}
        </div>
      )}
      </>)}
      {/* Prime Vision (business B2B) */}
      <PxSubmenuGroup
        title="Prime Vision"
        icon={Rocket}
        testid="nav-pv-group"
        active={loc.pathname.startsWith("/prime-vision")}
        sublinks={PRIMEVISION_SUBLINKS}
        onNavigate={onNavigate}
      />
      {/* Medico (professional development · owner only) */}
      {isOwner && (
        <PxSubmenuGroup
          title="Medico"
          icon={Stethoscope}
          testid="nav-medico-group"
          active={loc.pathname.startsWith("/medico")}
          sublinks={MEDICO_SUBLINKS}
          onNavigate={onNavigate}
        />
      )}
      {/* Documents expandable */}
      <button
        type="button"
        onClick={() => setDocumentsOpen((v) => !v)}
        className={[
          "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition w-full",
          documentsActive ? "bg-white shadow-sm" : "hover:bg-white/70",
        ].join(" ")}
        style={{ color: "var(--pxos-text-dark)" }}
        data-testid="nav-documents-group"
        title="Espandi/comprimi il menu Documenti"
        aria-expanded={documentsOpen}
      >
        <FileText size={17}/>
        <span className="truncate flex-1 text-left">Documenti</span>
        {documentsOpen ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
      </button>
      {documentsOpen && (
        <div className="pl-4 border-l ms-3.5 space-y-0.5" style={{ borderColor: "var(--pxos-border)" }} data-testid="nav-documents-submenu">
          {DOCUMENTS_SUBLINKS
            .filter((l) => !l.financeOnly || canSeeFinances)
            .map((l) => (
            <NavLink
              key={l.testid}
              to={l.to}
              end={l.end}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                ["flex items-center gap-2 rounded-xl px-3 py-1.5 text-[13px] transition",
                 isActive ? "bg-white shadow-sm font-semibold" : "hover:bg-white/70"].join(" ")
              }
              style={{ color: "var(--pxos-text-dark)" }}
            >
              <span className="truncate">{l.label}</span>
            </NavLink>
          ))}
        </div>
      )}
      <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest opacity-60"
           style={{ color: "var(--pxos-text-dark)" }}>Collegamenti esterni</div>
      <div className="border-t pt-1" style={{ borderColor: "var(--pxos-border)" }} data-testid="sidebar-external-links">
        {EXTERNAL_LINKS.map((l) => (
          <ExternalNavItem key={l.href} {...l} onNavigate={onNavigate} />
        ))}
      </div>
      <div className="mt-4 mb-1 px-3 text-[10px] uppercase tracking-widest opacity-60"
           style={{ color: "var(--pxos-text-dark)" }}>Sistema</div>
      <SideNavItem to="/settings" label="Impostazioni" icon={SettingsIcon} testid="nav-settings" onNavigate={onNavigate} />
    </nav>
  );
}

function PxSubmenuGroup({ title, icon: Icon, testid, active, sublinks, onNavigate }) {
  const [open, setOpen] = useState(active);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          "flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition w-full",
          active ? "bg-white shadow-sm" : "hover:bg-white/70",
        ].join(" ")}
        style={{ color: "var(--pxos-text-dark)" }}
        data-testid={testid}
        aria-expanded={open}
      >
        <Icon size={17} />
        <span className="truncate flex-1 text-left">{title}</span>
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      {open && (
        <div className="pl-4 border-l ms-3.5 space-y-0.5"
             style={{ borderColor: "var(--pxos-border)" }}
             data-testid={`${testid}-submenu`}>
          {sublinks.map((l) => (
            <NavLink
              key={l.testid}
              to={l.to}
              end={l.end}
              onClick={onNavigate}
              data-testid={l.testid}
              className={({ isActive }) =>
                ["flex items-center gap-2 rounded-xl px-3 py-1.5 text-[13px] transition",
                 isActive ? "bg-white shadow-sm font-semibold" : "hover:bg-white/70"].join(" ")}
              style={{ color: "var(--pxos-text-dark)" }}
            >
              <span className="truncate">{l.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </>
  );
}

function SideNavItem({ to, label, icon: Icon, testid, onNavigate, sub = false, end = false }) {  return (
    <NavLink
      to={to}
      end={to === "/" || end}
      onClick={onNavigate}
      data-testid={testid}
      className={({ isActive }) =>
        [
          "flex items-center gap-2.5 rounded-xl transition",
          sub ? "px-3 py-1.5 text-[13px] font-normal" : "px-3 py-2.5 text-sm font-medium",
          isActive ? "bg-white shadow-sm" : "hover:bg-white/70",
        ].join(" ")
      }
      style={{ color: "var(--pxos-text-dark)" }}
    >
      <Icon size={sub ? 14 : 17} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function ExternalNavItem({ href, label, icon: Icon, testid, tooltip, onNavigate }) {
  return (
    <Tip label={tooltip} side="right">
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
        data-testid={testid}
        className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition hover:bg-white/70"
        style={{ color: "var(--pxos-text-dark)" }}
      >
        <Icon size={17} />
        <span className="truncate flex-1">{label}</span>
        <ExternalLink size={12} className="opacity-50" aria-hidden="true" />
      </a>
    </Tip>
  );
}

function UserFooter({ user, onLogout }) {
  return (
    <div className="border-t px-4 py-3 space-y-2" style={{ borderColor: "var(--pxos-border)" }}>
      <div className="text-xs" style={{ color: "var(--pxos-text-dark)" }}>
        <div className="opacity-60 uppercase tracking-widest text-[9px]">Accesso</div>
        <div className="font-medium truncate">{user?.email}</div>
      </div>
      <button
        onClick={onLogout}
        className="w-full inline-flex items-center gap-1.5 justify-center rounded-full px-3 py-2 text-xs font-semibold bg-white/85 hover:bg-white transition"
        style={{ color: "var(--pxos-text-dark)" }}
        data-testid="logout-btn"
        title="Termina la sessione e torna al login"
      >
        <LogOut size={14} /> Esci
      </button>
    </div>
  );
}
